import { useState } from 'react'
import { X } from 'lucide-react'
import type { WireArticleBlock } from '../../api/types'
import {
  TABLE_VARIANT_LABELS,
  TABLE_BG_LABELS,
  TABLE_BG_DOT_CLASSES,
  type TableVariant,
  type TableBg,
} from '../tableVariants'

type Block = Extract<WireArticleBlock, { type: 'table' }>

interface Props {
  block: Block
  onUpdate: (updated: Block) => void
}

// ─── Colour swatch row ────────────────────────────────────────────────────────

const ALL_BGS = Object.keys(TABLE_BG_LABELS) as TableBg[]

function BgPicker({ value, onChange }: { value: TableBg | null; onChange: (v: TableBg | null) => void }) {
  return (
    <div className="flex items-center gap-1">
      {/* "None" option */}
      <button
        type="button"
        title="No colour"
        onClick={() => onChange(null)}
        className={`h-4 w-4 rounded-full border-2 bg-canvas transition-all ${value === null ? 'border-fg scale-110' : 'border-line'}`}
      />
      {ALL_BGS.map(bg => (
        <button
          key={bg}
          type="button"
          title={TABLE_BG_LABELS[bg]}
          onClick={() => onChange(bg)}
          className={`h-4 w-4 rounded-full border-2 transition-all ${TABLE_BG_DOT_CLASSES[bg]} ${value === bg ? 'border-fg scale-110' : 'border-transparent'}`}
        />
      ))}
    </div>
  )
}

// ─── Swatch chip (read-only, for the override list) ───────────────────────────

function BgChip({ bg }: { bg: TableBg }) {
  return (
    <span className={`inline-block h-3 w-3 rounded-full ${TABLE_BG_DOT_CLASSES[bg]}`} title={TABLE_BG_LABELS[bg]} />
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Ensure columnStyles is long enough to hold index ci. */
function ensureColStyles(block: Block, ci: number): Block['columnStyles'] {
  const styles = [...block.columnStyles]
  while (styles.length <= ci) styles.push({})
  return styles
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TableBlockPanel({ block, onUpdate }: Props) {
  const bodyRows = block.rows.filter(r => r.rowType === 'body')
  const colCount = (block.rows.find(r => r.rowType === 'body') ?? block.rows[0])?.cells?.length ?? 0

  // ── Add-row-override form state
  const [rowFormOpen, setRowFormOpen] = useState(false)
  const [pendingRowIdx, setPendingRowIdx] = useState(0)      // index into bodyRows
  const [pendingRowBg, setPendingRowBg] = useState<TableBg | null>(null)
  const [pendingRowBold, setPendingRowBold] = useState(false)

  // ── Add-col-override form state
  const [colFormOpen, setColFormOpen] = useState(false)
  const [pendingColIdx, setPendingColIdx] = useState(0)
  const [pendingColBg, setPendingColBg] = useState<TableBg | null>(null)
  const [pendingColAlign, setPendingColAlign] = useState<'left' | 'center' | 'right' | ''>('')

  // ── Existing overrides ─────────────────────────────────────────────────────

  // Find absolute row indices for body rows that have an override
  const rowOverrides = block.rows
    .map((row, absIdx) => ({ row, absIdx }))
    .filter(({ row }) => row.rowType === 'body' && (row.bg || row.bold || row.highlighted))

  const colOverrides = block.columnStyles
    .map((col, ci) => ({ col, ci }))
    .filter(({ col }) => col.bg || col.align)

  // ── Update helpers ─────────────────────────────────────────────────────────

  function setVariant(variant: TableVariant) {
    onUpdate({ ...block, variant })
  }

  function setHeaderColumn(checked: boolean) {
    onUpdate({ ...block, hasHeaderColumn: checked })
  }

  function removeRowOverride(absIdx: number) {
    const rows = block.rows.map((row, i) =>
      i === absIdx ? { ...row, bg: undefined, bold: undefined, highlighted: undefined } : row
    )
    onUpdate({ ...block, rows })
  }

  function removeColOverride(ci: number) {
    const columnStyles = ensureColStyles(block, ci)
    columnStyles[ci] = { ...columnStyles[ci], bg: undefined, align: undefined }
    onUpdate({ ...block, columnStyles })
  }

  function commitRowOverride() {
    if (pendingRowBg === null && !pendingRowBold) return
    // Find absolute index of this body row
    let bodyCount = 0
    const rows = block.rows.map(row => {
      if (row.rowType !== 'body') return row
      const match = bodyCount === pendingRowIdx
      bodyCount++
      if (!match) return row
      return { ...row, bg: pendingRowBg ?? undefined, bold: pendingRowBold || undefined }
    })
    onUpdate({ ...block, rows })
    setRowFormOpen(false)
    setPendingRowBg(null)
    setPendingRowBold(false)
    setPendingRowIdx(0)
  }

  function commitColOverride() {
    if (pendingColBg === null && !pendingColAlign) return
    const columnStyles = ensureColStyles(block, pendingColIdx)
    columnStyles[pendingColIdx] = {
      ...columnStyles[pendingColIdx],
      ...(pendingColBg ? { bg: pendingColBg } : {}),
      ...(pendingColAlign ? { align: pendingColAlign } : {}),
    }
    onUpdate({ ...block, columnStyles })
    setColFormOpen(false)
    setPendingColBg(null)
    setPendingColAlign('')
    setPendingColIdx(0)
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* ── Variant ── */}
      <div>
        <p className="mb-1 text-[9px] font-bold uppercase tracking-widest text-fg-secondary">Variant</p>
        <select
          value={block.variant ?? 'default'}
          onChange={e => setVariant(e.target.value as TableVariant)}
          className="w-full rounded border border-line bg-fill px-2 py-1 text-xs text-fg focus:border-accent-primary-line focus:outline-none"
        >
          {(Object.entries(TABLE_VARIANT_LABELS) as [TableVariant, string][]).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {/* ── Header column ── */}
      <label className="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={block.hasHeaderColumn ?? false}
          onChange={e => setHeaderColumn(e.target.checked)}
          className="accent-accent-primary-solid"
        />
        <span className="text-xs text-fg">Header column</span>
      </label>

      {/* ── Row overrides ── */}
      <div className="border-t border-line pt-3">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">Row overrides</p>
          {!rowFormOpen && bodyRows.length > 0 && (
            <button
              type="button"
              onClick={() => setRowFormOpen(true)}
              className="text-[10px] font-semibold text-accent-primary-fg hover:underline"
            >
              + Add
            </button>
          )}
        </div>

        {/* Existing row overrides */}
        {rowOverrides.map(({ row, absIdx }) => {
          // Display as body-row number (1-based)
          const bodyIdx = block.rows.slice(0, absIdx + 1).filter(r => r.rowType === 'body').length
          const effectiveBg = row.bg ?? (row.highlighted ? 'yellow' as TableBg : null)
          return (
            <div key={absIdx} className="flex items-center gap-2 py-1">
              <span className="w-10 shrink-0 text-xs text-fg">Row {bodyIdx}</span>
              <div className="flex flex-1 items-center gap-1.5">
                {effectiveBg && <BgChip bg={effectiveBg} />}
                {row.bold && <span className="text-[10px] font-bold text-fg-secondary">B</span>}
              </div>
              <button
                type="button"
                onClick={() => removeRowOverride(absIdx)}
                className="rounded p-0.5 text-fg-secondary hover:bg-fill hover:text-fg"
              >
                <X size={10} />
              </button>
            </div>
          )
        })}

        {/* Add row form */}
        {rowFormOpen && (
          <div className="mt-1 space-y-2 rounded border border-line bg-fill p-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-fg-secondary">Row</span>
              <select
                value={pendingRowIdx}
                onChange={e => setPendingRowIdx(Number(e.target.value))}
                className="flex-1 rounded border border-line bg-canvas px-1.5 py-0.5 text-xs text-fg focus:outline-none"
              >
                {bodyRows.map((_, i) => (
                  <option key={i} value={i}>{i + 1}</option>
                ))}
              </select>
            </div>
            <div>
              <p className="mb-1 text-[10px] text-fg-secondary">Colour</p>
              <BgPicker value={pendingRowBg} onChange={setPendingRowBg} />
            </div>
            <label className="flex cursor-pointer items-center gap-1.5">
              <input
                type="checkbox"
                checked={pendingRowBold}
                onChange={e => setPendingRowBold(e.target.checked)}
                className="accent-accent-primary-solid"
              />
              <span className="text-[10px] text-fg">Bold row</span>
            </label>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={commitRowOverride}
                disabled={pendingRowBg === null && !pendingRowBold}
                className="flex-1 rounded bg-accent-primary-solid px-2 py-0.5 text-[10px] font-semibold text-white disabled:opacity-40"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={() => { setRowFormOpen(false); setPendingRowBg(null); setPendingRowBold(false) }}
                className="rounded border border-line px-2 py-0.5 text-[10px] text-fg-secondary hover:bg-fill"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Column overrides ── */}
      <div className="border-t border-line pt-3">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">Column overrides</p>
          {!colFormOpen && colCount > 0 && (
            <button
              type="button"
              onClick={() => setColFormOpen(true)}
              className="text-[10px] font-semibold text-accent-primary-fg hover:underline"
            >
              + Add
            </button>
          )}
        </div>

        {/* Existing column overrides */}
        {colOverrides.map(({ col, ci }) => (
          <div key={ci} className="flex items-center gap-2 py-1">
            <span className="w-10 shrink-0 text-xs text-fg">Col {ci + 1}</span>
            <div className="flex flex-1 items-center gap-1.5">
              {col.bg && <BgChip bg={col.bg} />}
              {col.align && (
                <span className="text-[10px] text-fg-secondary">
                  {col.align === 'left' ? '←' : col.align === 'right' ? '→' : '↔'}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => removeColOverride(ci)}
              className="rounded p-0.5 text-fg-secondary hover:bg-fill hover:text-fg"
            >
              <X size={10} />
            </button>
          </div>
        ))}

        {/* Add column form */}
        {colFormOpen && (
          <div className="mt-1 space-y-2 rounded border border-line bg-fill p-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-fg-secondary">Col</span>
              <select
                value={pendingColIdx}
                onChange={e => setPendingColIdx(Number(e.target.value))}
                className="flex-1 rounded border border-line bg-canvas px-1.5 py-0.5 text-xs text-fg focus:outline-none"
              >
                {Array.from({ length: colCount }, (_, i) => (
                  <option key={i} value={i}>{i + 1}</option>
                ))}
              </select>
            </div>
            <div>
              <p className="mb-1 text-[10px] text-fg-secondary">Colour</p>
              <BgPicker value={pendingColBg} onChange={setPendingColBg} />
            </div>
            <div>
              <p className="mb-1 text-[10px] text-fg-secondary">Alignment</p>
              <div className="flex gap-1">
                {(['', 'left', 'center', 'right'] as const).map(a => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setPendingColAlign(a)}
                    className={`flex-1 rounded border px-1 py-0.5 text-[10px] transition-colors ${pendingColAlign === a ? 'border-accent-primary-line bg-accent-primary-fill text-fg' : 'border-line text-fg-secondary hover:bg-fill'}`}
                  >
                    {a === '' ? '—' : a === 'left' ? '←' : a === 'right' ? '→' : '↔'}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={commitColOverride}
                disabled={pendingColBg === null && !pendingColAlign}
                className="flex-1 rounded bg-accent-primary-solid px-2 py-0.5 text-[10px] font-semibold text-white disabled:opacity-40"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={() => { setColFormOpen(false); setPendingColBg(null); setPendingColAlign('') }}
                className="rounded border border-line px-2 py-0.5 text-[10px] text-fg-secondary hover:bg-fill"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  )
}
